import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Worker, Viewer } from '@react-pdf-viewer/core'
import { toolbarPlugin } from '@react-pdf-viewer/toolbar'
import { RenderedHtml } from 'components'
import { saveLearningHubBlockProgress } from 'api-actions'

import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/toolbar/lib/styles/index.css'

const workerUrl = `${
  typeof window !== 'undefined' ? window.location.origin : ''
}/pdf.worker.min.js`

const propTypes = {
  block: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    description: PropTypes.string,
    attachmentUrl: PropTypes.string,
    progress: PropTypes.shape({
      type: PropTypes.string,
      progress: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }).isRequired,
}

function jumpToSavedPage(jump, pageIndex) {
  if (pageIndex <= 0) return
  if (typeof jump !== 'function') return
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      jump(pageIndex)
    })
  })
}

function setRefCurrent(ref, value) {
  if (ref && typeof ref === 'object' && 'current' in ref) {
    ref.current = value
  }
}

function getRefCurrent(ref, fallback = null) {
  if (ref && typeof ref === 'object' && 'current' in ref) {
    return ref.current
  }
  return fallback
}

const LearningHubPdfBlockPanel = forwardRef(function LearningHubPdfBlockPanel(
  { block },
  ref
) {
  // toolbarPlugin (and nested plugins) use React hooks; must run every render — do not wrap in useMemo/useRef.
  const toolbarPluginInstance = toolbarPlugin()
  const { Toolbar } = toolbarPluginInstance
  const jumpToPage =
    toolbarPluginInstance.pageNavigationPluginInstance?.jumpToPage

  const fileUrl = block.attachmentUrl
  const initialPageIndex = useMemo(() => {
    const payload = block.progress || {}
    if (payload.type !== 'PDF') return 0
    const saved = Number(payload.progress)
    if (!Number.isFinite(saved) || saved < 1) return 0
    return Math.max(0, Math.floor(saved) - 1)
  }, [block.progress])

  const currentPageIndexRef = useRef(initialPageIndex)
  const prevBlockIdRef = useRef(block.id)

  if (getRefCurrent(prevBlockIdRef) !== block.id) {
    setRefCurrent(prevBlockIdRef, block.id)
    setRefCurrent(currentPageIndexRef, initialPageIndex)
  }

  useImperativeHandle(
    ref,
    () => ({
      completeAndAdvance: () =>
        saveLearningHubBlockProgress(block.id, {
          type: 'PDF',
          progress: Number(getRefCurrent(currentPageIndexRef, 0)) + 1,
          complete: true,
        }),
    }),
    [block.id]
  )

  return (
    <div className="learning-hub-block-player__panel learning-hub-block-player__panel--pdf">
      {block.description ? (
        <div className="learning-hub-block-player__block-description">
          <RenderedHtml>{block.description}</RenderedHtml>
        </div>
      ) : null}
      <div className="learning-hub-block-player__pdf-shell">
        <Worker workerUrl={workerUrl}>
          <div className="learning-hub-block-player__pdf-viewer-wrap">
            <Toolbar />
            <div className="learning-hub-block-player__pdf-viewer-inner">
              <Viewer
                key={block.id}
                fileUrl={fileUrl}
                plugins={[toolbarPluginInstance]}
                initialPage={initialPageIndex}
                onDocumentLoad={() => {
                  setRefCurrent(currentPageIndexRef, initialPageIndex)
                  jumpToSavedPage(jumpToPage, initialPageIndex)
                }}
                onPageChange={(e) => {
                  setRefCurrent(currentPageIndexRef, e.currentPage)
                }}
              />
            </div>
          </div>
        </Worker>
      </div>
    </div>
  )
})

LearningHubPdfBlockPanel.displayName = 'LearningHubPdfBlockPanel'

LearningHubPdfBlockPanel.propTypes = propTypes

export default LearningHubPdfBlockPanel
